import { useState } from 'react';

import { Button } from '@/components/ui';
import { YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import { RequestRow } from './request-row';
import { SentRow } from './sent-row';

import type { PendingEntry } from './connection-actions';
import type { RequestLines } from './use-request-lines';

const VISIBLE_LINES = 3;

/** One side of the requests card: a header with the number of lines that are open, the first three waiting lines, and
 * a button that unfolds the rest in place and folds them again. `entries` are the lines of the column in any status.
 * Above the stacking width the column takes half the card, also when the other side has no lines. */
export const RequestColumn = ({
  title,
  entries,
  count,
  lines,
  hasDivider,
}: {
  title: string;
  entries: PendingEntry[];
  /** The lines that have not closed. The header keeps saying 1 while the last one fades out. */
  count: number;
  lines: RequestLines;
  /** The column sits beside or under another, and a thin line sets them apart */
  hasDivider: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const waiting = entries.filter(
    ({ accountId }) => lines.statusOf(accountId) === 'waiting'
  );
  const shown = isExpanded ? waiting : waiting.slice(0, VISIBLE_LINES);
  // Answered lines stay listed until they have closed, and so do the declines that wait out their Undo
  const listed = entries.filter(
    (entry) =>
      lines.statusOf(entry.accountId) !== 'waiting' || shown.includes(entry)
  );
  const hiddenCount = waiting.length - shown.length;

  return (
    <YStack
      data-request-column
      className={cn(
        'w-1/2 min-w-0 flex-none pb-1.5 [@container(max-width:719px)]:w-full',
        hasDivider &&
          'border-l border-border [@container(max-width:719px)]:border-l-0 [@container(max-width:719px)]:border-t'
      )}
    >
      <span className="px-4 pb-1 pt-3 text-xs text-foreground-subtle">
        {`${title} · ${Math.max(count, 1)}`}
      </span>
      <YStack className="px-1.5">
        {listed.map((entry) =>
          entry.entry.state === 'accept' ? (
            <RequestRow key={entry.accountId} request={entry} lines={lines} />
          ) : (
            <SentRow key={entry.accountId} request={entry} lines={lines} />
          )
        )}
      </YStack>
      {(hiddenCount > 0 || (isExpanded && waiting.length > VISIBLE_LINES)) && (
        <Button
          variant="ghost"
          size="xs"
          textSize="xs"
          className="ml-3 mt-0.5 self-start text-foreground-muted"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? 'Show less' : `+${hiddenCount} more`}
        </Button>
      )}
    </YStack>
  );
};
